import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Amber (`primary`) is reserved for the primary action / risky launch controls (DESIGN-BRIEF §12). */
const button = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 pointer-coarse:min-h-11',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-fg-on-accent hover:bg-accent-hover active:bg-accent-active',
        secondary: 'border border-line bg-secondary text-fg hover:border-line-strong hover:bg-hover',
        ghost: 'text-fg-muted hover:bg-hover hover:text-fg',
        danger: 'border border-dnc/50 bg-dnc-bg text-dnc-fg hover:border-dnc',
      },
      size: { sm: 'h-7 px-2.5', md: 'h-8 px-3.5', icon: 'size-8 pointer-coarse:min-w-11' },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  loading?: boolean;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, asChild, disabled, children, type = 'button', ...p }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        {...(asChild ? {} : { type, disabled: disabled || loading })}
        aria-busy={loading || undefined}
        className={cn(button({ variant, size }), className)}
        {...p}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
